import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';

import GeoLocationComponent from 'components/GeoLocationComponent/GeoLocationComponent';

import generateUUID from 'utils/sharedUtils/generateUUID';
import { validateGeoLocationSize } from 'utils/validateGeoLocationSize';

import { useUpsertAor, useUpdateAor } from 'hooks/useAors';

import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import { TiptapRichTextEditor } from 'components/DesignSystems/New/TiptapRichTextEditor';
import { useAorText } from 'utils/aorTerminology';

export default function AORCreationDialog({
  show,
  onClose,
  existingAOR = null,
}) {
  const aorText = useAorText();
  const createAor = useUpsertAor();
  const updateAor = useUpdateAor();

  const [isLoaded, setIsLoaded] = useState(true);

  const reduxCurrentlySelectedGroup = useSelector((state) => {
    return state.app.currentlySelectedGroup;
  });

  const [name, setName] = useState(
    (!!existingAOR && !!existingAOR.name && existingAOR.name) || ''
  );
  const [description, setDescription] = useState(
    (!!existingAOR && !!existingAOR.description && existingAOR.description) ||
      ''
  );
  const [location, setLocation] = useState(existingAOR?.location);

  useEffect(() => {
    if (!isLoaded && (createAor.isSuccess || updateAor.isSuccess)) {
      onClose();
    }
  }, [createAor.isSuccess, updateAor.isSuccess, isLoaded, onClose]);

  function createAOR() {
    const { isValid, locationString } = validateGeoLocationSize(location);

    const aor = {
      id: (!!existingAOR && existingAOR.id) || generateUUID(),
      group_guid: reduxCurrentlySelectedGroup.group_guid,
      name: name,
      description: description,
      location: isValid ? locationString : null,
    };
    return aor;
  }

  const handleAORCreateClick = () => {
    setIsLoaded(false);
    const aor = createAOR();

    if (existingAOR) {
      updateAor.mutate(aor);
    } else {
      createAor.mutate(aor);
    }
  };

  const saveName = (!!existingAOR && existingAOR.name) || aorText('AOR');

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>
          {(!!existingAOR && <>Edit {existingAOR.name}</>) || (
            <>{aorText('New AOR')}</>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <label className="form-label">Name</label>
          <StylishNewInput
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Description</label>
          <TiptapRichTextEditor
            value={description}
            onChange={setDescription}
            placeholder="Enter description..."
            minHeight="150px"
          />
        </div>

        <div className="m-0">
          <label className="form-label">Location</label>
          <GeoLocationComponent location={location} setLocation={setLocation} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="button-group">
          <StylishNewButton
            type="button"
            className="button--secondary button--reverse"
            onClick={onClose}
          >
            Cancel
          </StylishNewButton>
          <StylishNewButton
            className="button--primary"
            disabled={!isLoaded || !name}
            onClick={() => handleAORCreateClick()}
          >
            Save {saveName}
          </StylishNewButton>
        </div>
      </Modal.Footer>
      {!isLoaded && <div>loading...</div>}
    </Modal>
  );
}
